import { fieldHasContent, groupFieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { CookieSetter } from 'remote-com-shared/utils/CookieSetter';
import { dataLayerPushArgs } from './helpers';

export const baseCookieControlConfig = {
  apiKey: '99a1c0a70ecd52f5742d9b2146a6f69e294a1962',
  product: 'PRO_MULTISITE',
  acceptBehaviour: 'all',
  closeStyle: 'button',
  necessaryCookies: [
    'remote_irclickid',
    'remote_utm_content',
    'remote_utm_campaign',
    'remote_utm_source',
    /**
     * Rudderstack specific cookies:
     *
     * See:
     * - https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/data-storage-cookies/#cookies
     * - https://www.rudderstack.com/docs/sources/event-streams/sdks/session-tracking/faq/ (for rl_session)
     */
    'rl_page_init_referring_domain',
    'rl_page_init_referrer',
    'rl_group_trait',
    'rl_trait',
    'rl_user_id',
    'rl_session',
    'rl_group_id',
    'rl_anonymous_id',
  ],
  // optionalCookies are overwritten by Prismic - https://all-remote-content.prismic.io/documents~k=cookie_control_config&b=working&c=published&l=en-us/YxlDSxEAAHzJm8Ti/
  optionalCookies: [
    {
      name: 'analytics',
      label: 'Analytics',
      description:
        'Analytics cookies help us understand how visitors interact with our website by collecting and reporting information about them to us on an aggregate level without revealing actual visitor identity.',
      onAccept() {
        dataLayerPushArgs({ event: 'analytics_consent_given' });
        CookieSetter.acceptGroupCookies('analytics');
      },
      onRevoke() {
        dataLayerPushArgs({ event: 'analytics_consent_revoked' });
        CookieSetter.denyGroupCookies('analytics');
      },
    },
    {
      name: 'marketing',
      label: 'Marketing',
      description:
        'Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual visitor and thereby more valuable for publishers and third party advertisers.',
      onAccept() {
        dataLayerPushArgs({ event: 'marketing_consent_given' });
        CookieSetter.acceptGroupCookies('marketing');
      },
      onRevoke() {
        dataLayerPushArgs({ event: 'marketing_consent_revoked' });
        CookieSetter.denyGroupCookies('marketing');
      },
    },
  ],
  statement: {
    description: 'Read',
    name: "Remote's Privacy Policy",
    url: 'https://remote.com/privacy-policy',
    updated: '15/07/2022',
  },
  initialState: 'notify',
  notifyDismissButton: false,
  position: 'right',
  theme: 'light',
  branding: {
    fontFamily: 'inherit',
    fontColor: '#617798',
    notifyFontColor: '#617798',
    fontSizeTitle: '18px',
    fontSizeIntro: '14px',
    fontSizeHeaders: '14px',
    fontSize: '14px',
    backgroundColor: '#fff',
    notifyBackgroundColor: '#fff',
    toggleText: '#fff',
    toggleColor: '#fff',
    toggleBackground: '#624DE3',
    removeIcon: false,
    removeAbout: true,
  },
  text: {
    // main preference panels
    title: 'This site uses cookies to store information on your computer.',
    intro:
      'Some of these cookies are essential, while others help us to ' +
      'improve your experience by providing insights into how ' +
      'the site is being used.',
    acceptSettings: 'Accept all',
    rejectSettings: 'Reject optional',
    necessaryTitle: 'Strictly Necessary',
    necessaryDescription:
      'Necessary cookies make our website operational by enabling core functions like page navigation and access to secure areas. This website cannot function properly without these cookies.',
    closeLabel: 'Save cookie preferences',
    cornerButton: 'Save cookie preferences',

    // main preference panel controls
    on: 'On',
    off: 'Off',
    thirdPartyTitle: 'Some cookies require your attention',
    thirdPartyDescription:
      'Consent for the following cookies could not be ' +
      'automatically revoked. Please follow the link(s) ' +
      'below to opt out manually.',

    // notification panels
    notifyTitle: 'Your choice regarding cookies on this site',
    notifyDescription:
      'Some of these cookies are essential, while others help us to improve our services and your experience by providing insights into how the site is being used.',
    accept: 'Accept all',
    reject: 'Reject optional',
    settings: 'Settings',
  },
};

export function cookieControlConfigFactory(cookieControlDocument) {
  const {
    title,
    intro,
    acceptSettings,
    rejectSettings,
    necessaryTitle,
    necessaryDescription,
    closeLabel,
    cornerButton,

    on,
    off,
    thirdPartyTitle,
    thirdPartyDescription,

    notifyTitle,
    notifyDescription,
    accept,
    reject,
    settings,

    statementDescription,
    statementName,
    statementUpdated,
    statementUrl,
    position,
    necessaryCookies,
    optionalCookies,
    buttonIcon,
  } = cookieControlDocument;

  const baseConfig = baseCookieControlConfig;

  if (fieldHasContent(title)) {
    baseConfig.text.title = title;
  }
  if (fieldHasContent(intro)) {
    baseConfig.text.intro = intro;
  }
  if (fieldHasContent(acceptSettings)) {
    baseConfig.text.acceptSettings = acceptSettings;
  }
  if (fieldHasContent(rejectSettings)) {
    baseConfig.text.rejectSettings = rejectSettings;
  }
  if (fieldHasContent(necessaryTitle)) {
    baseConfig.text.necessaryTitle = necessaryTitle;
  }
  if (fieldHasContent(necessaryDescription)) {
    baseConfig.text.necessaryDescription = necessaryDescription;
  }
  if (fieldHasContent(closeLabel)) {
    baseConfig.text.closeLabel = closeLabel;
  }
  if (fieldHasContent(cornerButton)) {
    baseConfig.text.cornerButton = cornerButton;
  }

  if (fieldHasContent(on)) {
    baseConfig.text.on = on;
  }
  if (fieldHasContent(off)) {
    baseConfig.text.off = off;
  }
  if (fieldHasContent(thirdPartyTitle)) {
    baseConfig.text.thirdPartyTitle = thirdPartyTitle;
  }
  if (fieldHasContent(thirdPartyDescription)) {
    baseConfig.text.thirdPartyDescription = thirdPartyDescription;
  }

  if (fieldHasContent(notifyTitle)) {
    baseConfig.text.notifyTitle = notifyTitle;
  }
  if (fieldHasContent(notifyDescription)) {
    baseConfig.text.notifyDescription = notifyDescription;
  }
  if (fieldHasContent(accept)) {
    baseConfig.text.accept = accept;
  }
  if (fieldHasContent(reject)) {
    baseConfig.text.reject = reject;
  }
  if (fieldHasContent(settings)) {
    baseConfig.text.settings = settings;
  }

  if (statementDescription) {
    baseConfig.statement.description = statementDescription;
  }
  if (statementName) {
    baseConfig.statement.name = statementName;
  }
  if (statementUrl?.url) {
    baseConfig.statement.url = statementUrl?.url;
  }
  const updatedDate = new Date(Date.parse(statementUpdated));
  if (statementUpdated) {
    baseConfig.statement.updated = updatedDate.toLocaleDateString('pt-PT');
  }

  if (fieldHasContent(necessaryCookies)) {
    const parsedCookies = necessaryCookies.split(',');

    baseConfig.necessaryCookies = parsedCookies;
  }
  if (groupFieldHasContent(optionalCookies, 'name')) {
    baseConfig.optionalCookies = optionalCookies.map(
      ({ name, label, cookies, description, onAcceptEvent, onRevokeEvent }) => {
        const parsedCookies = cookies.split(',');

        return {
          name,
          label,
          cookies: parsedCookies,
          description,
          onAccept() {
            dataLayerPushArgs({ event: onAcceptEvent });
            CookieSetter.acceptGroupCookies(name);
          },
          onRevoke() {
            dataLayerPushArgs({ event: onRevokeEvent });
            CookieSetter.denyGroupCookies(name);
          },
        };
      }
    );
  }

  baseConfig.position = position ? 'right' : 'left';

  if (buttonIcon) {
    baseConfig.branding.buttonIcon = buttonIcon.url;
    baseConfig.branding.buttonIconWidth = buttonIcon.dimensions.width;
    baseConfig.branding.buttonIconHeight = buttonIcon.dimensions.height;
  }

  return baseConfig;
}
